import React from "react";
import PropTypes from "prop-types";
import { SFStack } from "../../grid";
import SFTypography from "../../typography";
import SFTooltip from "../../tooltip";
import getStyled from "../../../hooks/getStyled";
import { highlightRangeInSeries } from "./helpers";
import CreateReactPortal from "../../../hooks/createReactPortal";

const { styled } = getStyled();

const LegendItem = styled("div", {
  shouldForwardProp: (prop) => prop !== "backgroundColor" && prop !== "loading",
})(({ theme, backgroundColor, loading }) => ({
  width: 30,
  height: 16,
  backgroundColor,
  cursor: "pointer",
  ...(loading && {
    pointerEvents: "none",
    opacity: 0.2,
  }),
}));

const SFTypographyStyled = styled(SFTypography, {
  shouldForwardProp: (prop) => prop !== "loading",
})(({ theme, loading }) => ({
  ...(loading && {
    opacity: 0.2,
  }),
}));

const CustomLegend = ({
  colorRange,
  chartId,
  legendFormatter,
  container,
  loading,
}) => {
  //if container is null, the legend will be rendered in the same container as the chart else it will be rendered in the container passed
  const disablePortal = container === null;

  const getLegendFormatter = (value) => {
    if (legendFormatter) {
      return legendFormatter(value);
    }
    return value;
  };

  return CreateReactPortal(
    <SFStack direction="column" width="fit-content">
      <SFStack direction="row" justifyContent="space-between">
        <SFTypographyStyled variant="tableListDescription" loading={loading}>
          {getLegendFormatter(colorRange[0].from)}
        </SFTypographyStyled>
        <SFTypographyStyled variant="tableListDescription" loading={loading}>
          {getLegendFormatter(colorRange[colorRange.length - 1].to)}
        </SFTypographyStyled>
      </SFStack>
      <SFStack direction="row" spacing={1}>
        {colorRange.map((range, index) => (
          <SFTooltip
            key={index}
            variant="blue"
            content={`${getLegendFormatter(range.from)} - ${getLegendFormatter(
              range.to
            )}`}
          >
            <LegendItem
              backgroundColor={range.color}
              onMouseMove={(e) =>
                highlightRangeInSeries(e, { colorsIndex: index, chartId })
              }
              onMouseOut={(e) =>
                highlightRangeInSeries(e, { colorsIndex: index, chartId })
              }
              loading={loading}
            />
          </SFTooltip>
        ))}
      </SFStack>
    </SFStack>,
    container,
    disablePortal
  );
};

CustomLegend.propTypes = {
  // The color range of the heatmap
  colorRange: PropTypes.array.isRequired,
  // The id of the chart
  chartId: PropTypes.string.isRequired,
  // The legend formatter
  legendFormatter: PropTypes.func,
  // The container where the legend will be rendered
  container: PropTypes.object,
};

export default CustomLegend;
